import { createSelector } from '@reduxjs/toolkit';

import { IProductCatalogState } from '@lib/core/products/slices/searchCatalog';

/**
 * Selects the entire product catalog state from the Redux store.
 */
export const selectSearchCatalogState = (state): IProductCatalogState => state.searchCatalog;

/**
 * Selects the products data from the product catalog state.
 */
export const selectProductsData = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.productsData,
);

/**
 * Selects the list of products from the products data.
 */
export const selectProductsDataResults = createSelector([selectProductsData], productsData => productsData.results);

/**
 * Selects the total count of products in the current query.
 */
export const selectProductsDataCount = createSelector([selectProductsData], productsData => productsData.count);

/**
 * Selects the featured products from the product catalog state.
 */
export const selectFeaturedProducts = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.featuredProducts,
);

/**
 * Selects the last time the featured products were loaded.
 */
export const selectLastFeaturedProductsLoadedTime = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.lastFeaturedProductsLoadedTime,
);

/**
 * Selects the last time any products data was loaded.
 */
export const selectLastDataLoadedTime = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.lastDataLoadedTime,
);

/**
 * Selects a boolean indicating whether the products request is currently loading.
 */
export const selectIsProductsRequestLoading = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.isProductsRequestLoading,
);

/**
 * Selects a boolean indicating whether the featured products are currently loading.
 */
export const selectIsFeaturedProductsLoading = createSelector(
  [selectSearchCatalogState],
  searchCatalogState => searchCatalogState.isFeaturedProductsLoading,
);
